import { createBrowserRouter, Navigate } from 'react-router'
import { ProtectedRoute } from '@/auth/ProtectedRoute'
import { AppLayout } from '@/components/AppLayout'
import { LoginPage } from '@/pages/LoginPage'
import { OauthConsentPage } from '@/pages/OauthConsentPage'
import { OauthErrorPage } from '@/pages/OauthErrorPage'
import { AcceptInvitePage } from '@/pages/AcceptInvitePage'
import { SharedProposalPage } from '@/pages/SharedProposalPage'
import { PrintProposalPage } from '@/pages/PrintProposalPage'
import { TeamPage } from '@/pages/TeamPage'
import { ClientsPage } from '@/pages/ClientsPage'
import { NewClientPage } from '@/pages/NewClientPage'
import { ClientDetailPage } from '@/pages/ClientDetailPage'
import { ProposalsPage } from '@/pages/ProposalsPage'
import { NewProposalPage } from '@/pages/NewProposalPage'
import { ProposalDetailPage } from '@/pages/ProposalDetailPage'
import { FieldMapPage } from '@/pages/FieldMapPage'
import { SiteMapAsOfPage } from '@/pages/SiteMapAsOfPage'
import { IrrigationMapPage } from '@/pages/IrrigationMapPage'
import { SiteBoundaryPage } from '@/pages/SiteBoundaryPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { AccountPage } from '@/pages/AccountPage'
import { ProposalDocumentPage } from '@/pages/ProposalDocumentPage'

/**
 * Application routes. `/login`, `/invite/:token`, `/p/:token`, `/print/:grant`,
 * and `/oauth/error` are public. The invite, shared proposal, and print links
 * have to be: the visitor has no account, and the token is the authorization.
 * `/oauth/error` is a connection that failed before the connect screen, shown
 * without a login and never sent back to the app. Everything else is nested
 * under `ProtectedRoute` (which redirects unauthenticated users to `/login`,
 * keeping the path and the query) and, except the full-screen routes below,
 * the `AppLayout` chrome.
 *
 * `/sites/:siteId/survey` and `/sites/:siteId/boundary` are protected but
 * deliberately outside `AppLayout` — see the comment on the routes.
 * `/oauth/consent` is too: it is the decision to let an app work as you, and
 * the product nav would bury where the code is about to be sent.
 *
 * `/` redirects to `/clients` — the book of business is the screen someone
 * wants on open, not the team roster.
 *
 * `/account` is reached from the signed-in address in the top bar, not from
 * the main nav: the nav lists areas of the product, and this is one person's
 * own things. `/settings` next to it is the organization's.
 *
 * `/clients/new` is declared before `/clients/:id` so "new" is never read as an
 * id. React Router ranks static segments above dynamic ones so this would work
 * either way, but relying on ordering that reads correctly is cheaper than
 * relying on a ranking rule the next reader has to recall.
 */
export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/invite/:token',
    element: <AcceptInvitePage />,
  },
  {
    /*
     * The link in a proposal email. Public for the same reason the invite
     * route is: the recipient is a client with no account, and the token is
     * the authorization.
     */
    path: '/p/:token',
    element: <SharedProposalPage />,
  },
  {
    /*
     * The page Cloudflare's browser prints a sent proposal from. Public
     * because that browser has no session; the short-lived grant in the URL,
     * minted by the API, is the authorization.
     */
    path: '/print/:grant',
    element: <PrintProposalPage />,
  },
  {
    // Public. A failure before the connect screen, and it stays on Smithwell.
    path: '/oauth/error',
    element: <OauthErrorPage />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      // Outside `AppLayout` on purpose: the survey screen is a full-viewport
      // map used one-handed outdoors, and the app's top bar plus the layout's
      // padding would spend a fifth of a phone screen on chrome it cannot use.
      { path: 'sites/:siteId/survey', element: <FieldMapPage /> },
      { path: 'sites/:siteId/irrigation', element: <IrrigationMapPage /> },
      { path: 'sites/:siteId/boundary', element: <SiteBoundaryPage /> },
      // A past visit's map, full screen like the survey it was drawn from.
      {
        path: 'sites/:siteId/visits/:visitId/map',
        element: <SiteMapAsOfPage />,
      },
      // Also outside the shell: this route renders the document a client
      // receives, and app chrome around it would make it impossible to tell
      // what they will actually see.
      {
        path: 'proposals/:id/versions/:versionId/document',
        element: <ProposalDocumentPage />,
      },
      // Outside the shell: the decision to let an app in, not a page of the product.
      { path: 'oauth/consent', element: <OauthConsentPage /> },
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/clients" replace /> },
          { path: 'clients', element: <ClientsPage /> },
          { path: 'clients/new', element: <NewClientPage /> },
          { path: 'clients/:id', element: <ClientDetailPage /> },
          {
            path: 'clients/:clientId/proposals/new',
            element: <NewProposalPage />,
          },
          { path: 'proposals', element: <ProposalsPage /> },
          { path: 'proposals/:id', element: <ProposalDetailPage /> },
          { path: 'team', element: <TeamPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'account', element: <AccountPage /> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])
